import PocketBase, { ClientResponseError, type RecordModel } from 'pocketbase';

// ══════════════════════════════════════════════════════════════════════════
//  Client PocketBase — remplace `services/supabase.ts`
// ══════════════════════════════════════════════════════════════════════════
//
// PERSISTANCE DE SESSION
// ----------------------
// On garde le `LocalAuthStore` par défaut du SDK : le jeton et l'enregistrement
// utilisateur sont écrits dans `localStorage` (clé `pocketbase_auth`). C'est le
// comportement qu'avait supabase-js — la session survit au refresh et est
// partagée entre les onglets du même origin.
//
// PÉRIMÈTRE
// ---------
// Ce client ne sert QU'À L'AUTHENTIFICATION (mot de passe, code par email,
// Google, réinitialisation, vérification d'adresse). Toutes les collections
// métier sont en deny-all côté PocketBase (cf. `pocketbase/pb_migrations/`),
// donc aucune donnée applicative ne transite par ce client : elle passe par
// le serveur Express, seul point d'autorisation (scope organisation, TOTP).

const POCKETBASE_URL =
  (import.meta.env.VITE_POCKETBASE_URL as string | undefined) ?? 'http://127.0.0.1:8090';

export const pb = new PocketBase(POCKETBASE_URL);

// Par défaut le SDK annule automatiquement une requête en vol si une autre part
// sur le même couple (méthode, chemin). React 19 StrictMode monte chaque effet
// deux fois en dev : la seconde requête tuerait la première et on récupérerait
// une exception « autocancelled » à la place d'une session. On désactive donc
// l'auto-cancel — c'est l'équivalent fonctionnel du contournement de deadlock
// qu'imposait le verrou interne de supabase-js.
pb.autoCancellation(false);

/** Nom de la collection auth (ex-table `profiles` fusionnée dedans). */
export const USERS_COLLECTION = 'users';

/**
 * Vue applicative de la session, volontairement minimale.
 *
 * Le reste de l'app ne consomme que `token` (en-tête `Authorization`). Le
 * profil métier (organisation, plan, rôle…) vient de `GET /api/auth/me`, pas
 * de l'enregistrement PocketBase — c'est le serveur qui fait autorité.
 */
export interface Session {
  token: string;
  record: RecordModel | null;
}

/** Session courante lue depuis l'authStore, ou `null` si aucun jeton valide. */
export function currentSession(): Session | null {
  if (!pb.authStore.isValid || !pb.authStore.token) return null;
  return { token: pb.authStore.token, record: pb.authStore.record ?? null };
}

/**
 * Traduit une erreur SDK en message affichable.
 *
 * PocketBase renvoie ses messages en anglais et souvent très génériques
 * (« Failed to authenticate. »). On préfère systématiquement le `fallback`
 * français fourni par l'appelant, sauf pour les deux cas où le statut porte une
 * information que l'appelant ne peut pas deviner : serveur injoignable et
 * rate-limit.
 */
export function pbErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ClientResponseError) {
    if (err.status === 0) return "Serveur d'authentification injoignable. Réessayez dans un instant.";
    if (err.status === 429) return 'Trop de tentatives. Réessayez dans quelques minutes.';
    return fallback;
  }
  return fallback;
}

/**
 * Fournisseurs OAuth2 réellement activés côté PocketBase (« google », …).
 *
 * Sert à n'afficher un bouton de connexion tierce que s'il peut aboutir. Le
 * jour où le fournisseur est activé (GOOGLE_OAUTH_CLIENT_ID côté PocketBase),
 * le bouton réapparaît sans nouvelle livraison. Ne jette jamais : un échec
 * réseau se lit « aucun fournisseur ».
 */
export async function listOAuthProviders(): Promise<string[]> {
  try {
    const methods = await pb.collection(USERS_COLLECTION).listAuthMethods();
    return (methods.oauth2?.providers ?? []).map((p) => p.name);
  } catch {
    return [];
  }
}

/**
 * Consomme le jeton de vérification d'email envoyé à l'inscription.
 * Les modèles de `pocketbase/mail-templates/` pointent vers
 * `/verify-email?token=…`, que `AuthGate` intercepte pour appeler ceci.
 * Ne demande aucune session : le jeton porte à lui seul la preuve.
 */
export async function confirmEmailVerification(token: string): Promise<void> {
  await pb.collection(USERS_COLLECTION).confirmVerification(token);
}

/**
 * Confirme un changement d'adresse email (lien `/confirm-email-change?token=…`).
 * PocketBase exige le mot de passe en plus du jeton.
 */
export async function confirmEmailChange(token: string, password: string): Promise<void> {
  await pb.collection(USERS_COLLECTION).confirmEmailChange(token, password);
}

// ── Session MFA ───────────────────────────────────────────────────────────
// Secret remis par Express quand un code TOTP est accepté (`/mfa/challenge`,
// `/mfa/enroll/confirm`), renvoyé dans l'en-tête `X-Evinly-MFA-Session`. Vit
// à côté du jeton (même exposition, même durée de vie côté client) : on le
// jette à la déconnexion et quand le jeton est invalidé.
const MFA_SESSION_KEY = 'evinly_mfa_session';

export function getMfaSession(): string | null {
  try {
    return localStorage.getItem(MFA_SESSION_KEY);
  } catch {
    return null;
  }
}

export function setMfaSession(secret: string): void {
  try {
    localStorage.setItem(MFA_SESSION_KEY, secret);
  } catch {
    // stockage indisponible (navigation privée stricte) : le code sera redemandé
  }
}

export function clearMfaSession(): void {
  try {
    localStorage.removeItem(MFA_SESSION_KEY);
  } catch {
    // rien à faire
  }
}

/** En-têtes d'authentification pour Express : jeton PocketBase + session MFA. */
export function authHeaders(): Record<string, string> {
  if (!pb.authStore.isValid || !pb.authStore.token) return {};
  const headers: Record<string, string> = { Authorization: `Bearer ${pb.authStore.token}` };
  const mfa = getMfaSession();
  if (mfa) headers['X-Evinly-MFA-Session'] = mfa;
  return headers;
}
